import type { LaserficheConnection } from "@repo/types";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import * as yup from "yup";
import { Formik } from "formik";
import { Button } from "@repo/ui/components/Base/Button";
import { useState } from "react";
import type { HttpResponse } from "@repo/shared/api";
import type { OnlyRequire } from "@repo/lib/types";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { cn } from "@repo/shared/utils";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

// prettier-ignore
type LaserficheConnectionAll =
  & Omit<LaserficheConnection & { hostingType: "self" }, "hostingType">
  & Omit<LaserficheConnection & { hostingType: "cloud" }, "hostingType">
  & { hostingType: "self" | "cloud" };

export interface LaserficheConnectProps {
  disabled?: boolean;
  repositoryDisabled?: boolean;
  initialValues?: OnlyRequire<LaserficheConnection, "hostingType">;
  connectLabel?: string;
  onConnect?: (connection: LaserficheConnection) => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
}

export const LaserficheConnect = ({
  disabled = false,
  repositoryDisabled,
  initialValues,
  connectLabel = "Connect",
  onConnect,
  onCancel,
}: LaserficheConnectProps) => {
  const [connectError, setConnectError] = useState("");

  const validationSchema = yup.object({
    hostingType: yup.string().oneOf(["self", "cloud"]),

    // when hostingType === "self"
    baseUrl: yup.string().when("hostingType", {
      is: "self",
      then: (schema) =>
        schema
          .required("Domain is required.")
          .matches(
            /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/[^/]+)*\/?$/,
            "You must provide a valid domain."
          ),
      otherwise: (schema) => schema.transform(() => undefined),
    }),
    repository: yup.string().when("hostingType", {
      is: "self",
      then: (schema) => schema.required("Repository name is required."),
      otherwise: (schema) => schema.transform(() => undefined),
    }),
    username: yup.string().when("hostingType", {
      is: "self",
      then: (schema) =>
        schema.required(
          "Username is required. This will be hidden after connection is established."
        ),
      otherwise: (schema) => schema.transform(() => undefined),
    }),
    password: yup.string().when("hostingType", {
      is: "self",
      then: (schema) =>
        schema.required(
          "Password is required. This will be hidden after connection is established."
        ),
      otherwise: (schema) => schema.transform(() => undefined),
    }),

    // when hostingType === "cloud"
    servicePrincipalKey: yup.string().when("hostingType", {
      is: "cloud",
      then: (schema) =>
        schema.required(
          "Service Principal Key is required. This will be hidden after connection is established."
        ),
      otherwise: (schema) => schema.transform(() => undefined),
    }),
    accessKey: yup.string().when("hostingType", {
      is: "cloud",
      then: (schema) =>
        schema.required(
          "Access Key is required. This will be hidden after connection is established."
        ),
      otherwise: (schema) => schema.transform(() => undefined),
    }),
  });

  const [showRepositoryIdHelp, setShowRepositoryIdHelp] = useState(false);
  const [showServicePrincipalHelp, setShowServicePrincipalHelp] =
    useState(false);
  const [showAccessKeyHelp, setShowAccessKeyHelp] = useState(false);

  return (
    <Formik
      initialValues={
        {
          hostingType: "self",
          baseUrl: "",
          repository: "",
          nickname: "",
          username: "",
          password: "",
          accessKey: "",
          servicePrincipalKey: "",
          ...initialValues,
        } satisfies LaserficheConnectionAll as LaserficheConnectionAll
      }
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        let connection: LaserficheConnection | null = null;

        if (values.hostingType === "self") {
          connection = {
            hostingType: "self",
            baseUrl: values.baseUrl,
            nickname: values.nickname,
            repository: values.repository,
            username: values.username,
            password: values.password,
          };
          // re-format base url

          // strip trailing /
          connection.baseUrl = connection.baseUrl.replace(/\/+$/g, "");
          // prefix protocol if missing
          if (!/^https?:\/\//.test(connection.baseUrl)) {
            connection.baseUrl = "https://" + connection.baseUrl;
          }

          // test connection
          try {
            await onConnect?.(connection);
          } catch (err) {
            if (err instanceof Response) {
              // connection doesn't work: notify user with proper warning
              const message = (
                err as HttpResponse<undefined, { details: string }>
              )?.error?.details;
              setConnectError(
                /fetch failed/i.test(message)
                  ? "Request failed. Check your domain."
                  : /status code 404/i.test(message)
                    ? "Status code 404. Check your domain."
                    : message ?? "An error occurred logging in."
              );
              console.warn(err);
            } else {
              setConnectError("An error occurred logging in.");
              console.error(err);
            }
            return;
          }
        } else if (values.hostingType === "cloud") {
          connection = {
            hostingType: "cloud",
            nickname: values.nickname,
            repository: values.repository,
            servicePrincipalKey: values.servicePrincipalKey,
            accessKey: values.accessKey,
          };

          // test connection
          try {
            await onConnect?.(connection);
          } catch (err) {
            if (err instanceof Response) {
              // connection doesn't work: notify user with proper warning
              const message = (
                err as HttpResponse<undefined, { details: string }>
              )?.error?.details;
              setConnectError(`An error occurred: ${message}`);
              console.warn(err);
            } else {
              setConnectError(
                "An error occurred logging in. Check your credentials."
              );
              console.error(err);
            }
            return;
          }
        }
        setConnectError("");
      }}
    >
      {({
        handleSubmit,
        setFieldValue,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        isSubmitting,
      }) => {
        return (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <p className="text-lg font-bold">
              {repositoryDisabled ? "Update connection" : "Set up a connection"}
            </p>

            <SelectInput
              disabled={disabled || repositoryDisabled || isSubmitting}
              label="Laserfiche Account Type"
              value={values.hostingType}
              onValueChange={(value) => setFieldValue("hostingType", value)}
              options={[
                <SelectItem key="self" value="self">
                  Laserfiche Self-Hosted
                </SelectItem>,
                <SelectItem key="cloud" value="cloud">
                  Laserfiche Cloud
                </SelectItem>,
              ]}
              error={!!errors.hostingType && touched.hostingType}
              errorMessage={errors.hostingType}
            />

            <Input
              disabled={disabled || isSubmitting}
              id="nickname"
              name="nickname"
              label="Login Nickname"
              value={values.nickname ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.nickname && touched.nickname}
              errorMessage={errors.nickname}
            />
            <p className="text-sm">
              The nickname is optional, and for reference only. The Laserfiche{" "}
              {values.hostingType === "self"
                ? "username and password"
                : "access key"}{" "}
              will be hidden after connecting. The nickname can be used for
              recalling which login was used.
            </p>

            <Input
              disabled={disabled || repositoryDisabled || isSubmitting}
              id="repository"
              name="repository"
              label="Repository Name"
              containerStyle="flex-grow"
              value={values.repository}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.repository && touched.repository}
              errorMessage={errors.repository}
            />
            {values.hostingType === "self" ? (
              <p className="text-sm">
                The repository ID is normally selected when logging into your
                self-hosted Laserfiche account. Simply copy this ID into the
                field above.
              </p>
            ) : (
              <div className="flex flex-col gap-2 text-sm">
                <p>To find your repository ID for Laserfiche Cloud,</p>

                <ol
                  className={cn(
                    "list-decimal pl-5 [&_li]:mb-2",
                    !showRepositoryIdHelp && "hidden"
                  )}
                >
                  <li>Visit your Laserfiche dashboard.</li>
                  <li>Click the four-squares icon from the top-right.</li>
                  <li>
                    Under "Content Management" pick "Repository Administration".
                  </li>
                  <li>
                    From the "Overview" section under the repository name, look
                    for the "Summarization" block on the page.
                  </li>
                  <li>
                    Copy the string next to "Repository ID" into the field
                    above.
                  </li>
                </ol>
                <button
                  type="button"
                  className="text-left cursor-pointer text-blue-500"
                  onClick={() => setShowRepositoryIdHelp((show) => !show)}
                >
                  {showRepositoryIdHelp ? (
                    <>
                      Show less{" "}
                      <ChevronUpIcon className="inline-block" size={16} />
                    </>
                  ) : (
                    <>
                      Show more{" "}
                      <ChevronDownIcon className="inline-block" size={16} />
                    </>
                  )}
                </button>
              </div>
            )}

            {values.hostingType === "self" && (
              <>
                <Input
                  disabled={disabled || repositoryDisabled || isSubmitting}
                  id="domain"
                  name="baseUrl"
                  label="Self-hosting Domain"
                  containerStyle="flex-grow"
                  value={values.baseUrl}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.baseUrl && touched.baseUrl}
                  errorMessage={errors.baseUrl}
                />

                <Input
                  disabled={disabled || isSubmitting}
                  id="username"
                  name="username"
                  label="Username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.username && touched.username}
                  errorMessage={errors.username}
                />

                <Input
                  disabled={disabled || isSubmitting}
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.password && touched.password}
                  errorMessage={errors.password}
                />
              </>
            )}

            {values.hostingType === "cloud" && (
              <>
                <Input
                  disabled={disabled || isSubmitting}
                  id="servicePrincipalKey"
                  name="servicePrincipalKey"
                  label="Service Principal Key"
                  type="password"
                  value={values.servicePrincipalKey}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    !!errors.servicePrincipalKey && touched.servicePrincipalKey
                  }
                  errorMessage={errors.servicePrincipalKey}
                />
                <div className="flex flex-col gap-2 text-sm">
                  <p>For the Service Principal Key,</p>

                  <div className={cn(!showServicePrincipalHelp && "hidden")}>
                    <p className="mb-2">
                      To get a Service Principal Key you must add a Service
                      Principal from Laserfiche. The only time the key is
                      displayed is during creation or key rotation. You can have
                      more than one Service Principal per repository.
                    </p>
                    <p className="mb-2">
                      Note: Do not rotate an existing key unless it is
                      specifically for <strong>your</strong> CiviDocs
                      connection; otherwise, you could disconnect another user
                      account or service. Rotating a key provides a new key
                      value. You also have to set the Access rights for this
                      Service Principal. CiviDocs minimally requires read/write
                      access to the repository provided above.
                    </p>

                    <ol className="list-decimal pl-5 [&_li]:mb-2">
                      <li>Visit your Laserfiche dashboard.</li>
                      <li>Click the four-squares icon from the top-right.</li>
                      <li>Under "Account Administration" pick "Account".</li>
                      <li>
                        In the "Users" section, switch to the "Service
                        Principals" tab.
                      </li>
                      <li>
                        Add a Service Principal, and configure its name and
                        permissions. Minimally enable read/write access to the
                        repository.
                      </li>
                      <li>Click "Create Service Principal Key(s)".</li>
                      <li>
                        From the dialog that opens, copy one of the keys to this
                        field.
                      </li>
                    </ol>
                  </div>
                  <button
                    type="button"
                    className="text-left cursor-pointer text-blue-500"
                    onClick={() => setShowServicePrincipalHelp((show) => !show)}
                  >
                    {showServicePrincipalHelp ? (
                      <>
                        Show less{" "}
                        <ChevronUpIcon className="inline-block" size={16} />
                      </>
                    ) : (
                      <>
                        Show more{" "}
                        <ChevronDownIcon className="inline-block" size={16} />
                      </>
                    )}
                  </button>
                </div>

                <TextArea
                  disabled={disabled || isSubmitting}
                  id="accessKey"
                  name="accessKey"
                  label="Access Key"
                  value={values.accessKey}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.accessKey && touched.accessKey}
                  errorMessage={errors.accessKey}
                />
                <div className="flex flex-col gap-2 text-sm">
                  <p>For the Access Key,</p>

                  <ol
                    className={cn(
                      "list-decimal pl-5 [&_li]:mb-2",
                      !showAccessKeyHelp && "hidden"
                    )}
                  >
                    <li>Visit your Laserfiche dashboard.</li>
                    <li>Click the four-squares icon from the top-right.</li>
                    <li>
                      Under "Account Administration" pick "Developer Console".
                    </li>
                    <li>
                      In the "Applications" section, click the "New" button to
                      the right of the page.
                    </li>
                    <li>
                      Pick "Create a new app", select "Service" as the app type,
                      and click "Create application".
                    </li>
                    <li>
                      Under the app configuration "General" tab, for the
                      "Service Account" field, select the name of the Service
                      Principal created for the "Service Principal Key" field
                      above.
                    </li>
                    <li>Click "Save" at the bottom.</li>
                    <li>Switch to the "Authentication" tab.</li>
                    <li>Under "Access Keys", click "Create key".</li>
                    <li>
                      Pick the "Create a public 'Access Key'..." option, and
                      click "Create key" at the bottom.
                    </li>
                    <li>
                      You will be presented with JSON data. From the top, click
                      "Copy", and paste the contents in this field.
                    </li>
                  </ol>
                  <button
                    type="button"
                    className="text-left cursor-pointer text-blue-500"
                    onClick={() => setShowAccessKeyHelp((show) => !show)}
                  >
                    {showAccessKeyHelp ? (
                      <>
                        Show less{" "}
                        <ChevronUpIcon className="inline-block" size={16} />
                      </>
                    ) : (
                      <>
                        Show more{" "}
                        <ChevronDownIcon className="inline-block" size={16} />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            <div className="flex flex-row justify-between">
              {onCancel ? (
                <Button
                  variant="destructive"
                  onClick={onCancel}
                  disabled={disabled || isSubmitting}
                >
                  Cancel
                </Button>
              ) : (
                <div />
              )}

              {!!connectError && (
                <p className="text-destructive">{connectError}</p>
              )}

              <Button type="submit" disabled={disabled || isSubmitting}>
                {connectLabel}
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
